/**
 * Renderer - graphics rendering pipeline for Genesys. Manages WebGL/WebGPU backends,
 * canvas DOM integration, XR support, and screen percentage scaling.
 */

import * as THREE from 'three';

import { CVarManager } from '../systems/ConsoleVariableManager.js';

import { GenesysWebGLRenderer } from './GenesysWebGLRenderer.js';
import { GenesysWebGPURenderer, type WebGPURendererParameters } from './GenesysWebGPURenderer.js';
import { DefaultRendererType } from './IGenesysRenderer.js';

import type { IGenesysRenderer, RendererType } from './IGenesysRenderer.js';
import type { World } from '../game/World.js';

// Re-export types
export type { IGenesysRenderer, RendererType, ShadowMapConfig } from './IGenesysRenderer.js';
export type { WebGPURenderer } from 'three/webgpu';
export type { WebGPURendererParameters } from './GenesysWebGPURenderer.js';
export { GenesysWebGLRenderer } from './GenesysWebGLRenderer.js';
export { GenesysWebGPURenderer } from './GenesysWebGPURenderer.js';

const defaultScreenPercentage = 100;

const cvarScreenPercentage = CVarManager.registerVariable({
  name: 'r.ScreenPercentage',
  default: defaultScreenPercentage,
  description: 'Screen percentage for the GenesysRenderer',
});

/** Options for creating a Renderer */
export interface RendererOptions {
  /** Renderer type: 'webgl', 'webgpu', or 'auto' (default: 'auto') */
  rendererType?: RendererType;
  /** WebGL-specific parameters */
  webgl?: THREE.WebGLRendererParameters;
  /** WebGPU-specific parameters */
  webgpu?: WebGPURendererParameters;
  /** XR/VR enabled (forces WebGL as WebGPU XR is experimental) */
  xrEnabled?: boolean;
}

/** Check if WebGPU is available */
export function isWebGPUAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'gpu' in navigator;
}

/** Manages graphics rendering pipeline with WebGL/WebGPU backends */
export class Renderer {
  readonly native: IGenesysRenderer;
  private abortController = new AbortController();
  private xrAnimationLoop: ((time: number) => void) | null = null;
  private onScreenPercentageChanged: ((oldValue: number, newValue: number) => void) | null = null;

  protected container: HTMLElement;

  private constructor(
    container: HTMLElement,
    nativeRenderer: IGenesysRenderer,
  ) {
    this.container = container;
    this.native = nativeRenderer;
    this.initializeRenderer();
  }

  /**
   * Factory method to create a Renderer.
   * @param container - HTML element to attach the renderer to
   * @param options - Renderer options
   * @returns Initialized Renderer
   */
  public static async create(container: HTMLElement, options?: RendererOptions): Promise<Renderer> {
    let rendererType = options?.rendererType ?? DefaultRendererType;

    // Force WebGL when XR is enabled (WebGPU XR support is still experimental)
    if (options?.xrEnabled) {
      if (rendererType === 'webgpu') {
        console.warn('[Renderer] XR is enabled but WebGPU was requested. Falling back to WebGL as WebGPU XR is experimental.');
      }
      rendererType = 'webgl';
    }

    // Default to WebGPU when auto is selected,
    // we'll use webgpu renderer's automatic webgl fallback no need to check isWebGPUAvailable().
    if (rendererType === 'auto') {
      rendererType = 'webgpu';
    }

    let native: IGenesysRenderer;
    if (rendererType === 'webgl') {
      native = await GenesysWebGLRenderer.create(options?.webgl);
    } else {
      native = await GenesysWebGPURenderer.create(options?.webgpu);
    }
    return new Renderer(container, native);
  }

  private initializeRenderer(): void {
    this.native.shadowMap.enabled = true;
    this.native.shadowMap.type = THREE.PCFShadowMap;
    this.native.toneMapping = THREE.NoToneMapping;

    window.addEventListener('resize', this.onWindowResize.bind(this), { signal: this.abortController.signal });
    this.onWindowResize();

    this.container.appendChild(this.native.domElement);

    const currentScreenPercentage = cvarScreenPercentage.get();
    if (currentScreenPercentage !== defaultScreenPercentage) {
      // current != default, means the value has been changed by the user, we must respect it
      this.native.setPixelRatio(currentScreenPercentage / 100);
    } else {
      // not changed by the user, update with the actual value
      cvarScreenPercentage.set(this.native.getPixelRatio() * 100);
    }
    this.onScreenPercentageChanged = (oldValue, newValue) => {
      this.native.setPixelRatio(newValue / 100);
    };
    cvarScreenPercentage.onValueChanged.add(this.onScreenPercentageChanged);
  }

  public getRendererType(): RendererType {
    return this.native.asWebGPU() ? 'webgpu' : 'webgl';
  }

  public destroy(): void {
    if (this.onScreenPercentageChanged) {
      cvarScreenPercentage.onValueChanged.remove(this.onScreenPercentageChanged);
      this.onScreenPercentageChanged = null;
    }
    // Stop any animation loop before disposal to prevent callbacks during cleanup
    this.native.setAnimationLoop(null);

    this.native.domElement.remove();
    this.abortController.abort();
    this.native.dispose();
  }

  public getWindowSize(): { width: number, height: number } {
    return {
      width: Math.floor(this.container.clientWidth || window.innerWidth),
      height: Math.floor(this.container.clientHeight || window.innerHeight)
    };
  }

  public render(world: World): void {
    // use manual label to avoid the name clash with the Renderer class in three.js
    THREE.ProfilerService.begin('GenesysRenderer.render');

    this.native.beginRenderFrame();
    // World.renderScene now accepts IGenesysRenderer
    world.renderScene(this.native, this.getWindowSize());
    this.native.endRenderFrame();

    THREE.ProfilerService.end('GenesysRenderer.render');
  }

  /**
   * Enable XR mode.
   * @param referenceSpaceType - 'local', 'local-floor', 'bounded-floor', or 'unbounded'
   */
  public enableXR(referenceSpaceType: XRReferenceSpaceType = 'local-floor'): void {
    this.native.xr.enabled = true;
    this.native.xr.setReferenceSpaceType(referenceSpaceType);
    console.log(`WebXR enabled with reference space: ${referenceSpaceType}`);
  }

  public disableXR(): void {
    this.native.xr.enabled = false;
    if (this.xrAnimationLoop) {
      this.native.setAnimationLoop(null);
      this.xrAnimationLoop = null;
    }
  }

  /**
   * Setup XR animation loop for VR rendering.
   * @param tickCallback - Game loop tick function
   */
  public setupXRAnimationLoop(tickCallback: (time: number) => void): void {
    this.xrAnimationLoop = tickCallback;
    this.native.setAnimationLoop(this.xrAnimationLoop);
  }

  public isXREnabled(): boolean {
    return this.native.xr.enabled;
  }

  public isXRPresenting(): boolean {
    return this.native.xr.isPresenting;
  }

  //====================================================================================================

  protected onWindowResize(): void {
    const windowSize = this.getWindowSize();
    this.native.setSize(windowSize.width, windowSize.height);
  }
}
